import { z } from 'zod';
/**
 * Entitlement catalog + per-tier defaults + resolver (Phase D-3, SDD §2.4
 * amended v1.0.5). Entitlements are per-tenant feature flags and quotas that
 * determine what a tenant can use beyond their subscription tier.
 *
 * 12 boolean feature flags + 1 numeric quota (`max_employees`) = 13 keys.
 *
 * IMPORTANT — when adding a new key, update in lockstep:
 *   1. ENTITLEMENT_KEYS (this file)
 *   2. TIER_DEFAULTS (all three tiers — TypeScript enforces the Record is total)
 *   3. SDD §2.4 catalog table
 *   4. the resolver's value-type handling if the new key's shape differs
 * And when a reserved module SHIPS, add its key to BUILT_ENTITLEMENT_KEYS (so
 * the trial offers it). See ADR-0016 for the tier classification.
 */
export const ENTITLEMENT_KEYS = [
    // Boolean feature flags
    'scheduled_payroll', // RESERVED — future scheduled/recurring payroll. The
    // current manual payroll engine is ungated/core (ADR-0016). Renamed from
    // `automated_payroll` (the old name was a misnomer — nothing is automated yet).
    'multi_country_payroll',
    'advanced_reports',
    'ats',
    'ewa', // EWA earned-wage-access draw (ADR-0009 amendment D9-A9). Growth-tier
    // upsell; declared-not-wired (no requirePlan enforcement). SHIPPED in Phase 3b
    // → now in BUILT_ENTITLEMENT_KEYS so trials offer it.
    'time_tracking',
    'expense_management', // Expense reimbursements (ADR-0020). Growth-tier upsell;
    // declared-not-wired (no requirePlan enforcement). SHIPPED Phases 1–4
    // → now in BUILT_ENTITLEMENT_KEYS so trials offer it (D-10).
    'asset_management',
    'training_module',
    'performance_reviews',
    'employee_self_service',
    'api_access',
    'sso_integration',
    // Numeric quotas
    'max_employees',
];
export const EntitlementKey = z.enum(ENTITLEMENT_KEYS);
export const EntitlementValueSchema = z.union([
    z.boolean(),
    z.number().int().nonnegative(),
    z.null(),
]);
/**
 * Per-tier default value for every entitlement key. Source of truth for which
 * features each tier includes (STRATEGY §3.1/§3.2/§3.4). Mutable only via PR +
 * review — there is no DB table of defaults (locked decision Q2, 2026-06-08).
 *
 * `max_employees`: Starter caps at 50; Growth/Enterprise are `null` (unlimited).
 */
export const TIER_DEFAULTS = {
    starter: {
        scheduled_payroll: false, // reserved future feature
        multi_country_payroll: false,
        advanced_reports: false,
        ats: false,
        ewa: false, // EWA is a Growth upsell (D9-A9)
        time_tracking: true, // ADR-0016: Attendance/time-tracking on Starter (magnet — switchers)
        expense_management: false,
        asset_management: false,
        training_module: false,
        performance_reviews: false,
        employee_self_service: true, // included even on Starter
        api_access: false,
        sso_integration: false,
        max_employees: 50,
    },
    growth: {
        scheduled_payroll: true, // reserved future feature — Growth+ when built (kept the old tier shape on rename)
        multi_country_payroll: false, // Enterprise-only
        advanced_reports: true,
        ats: true, // ADR-0012 D-11: Recruitment is a Growth-tier upsell (Growth + Enterprise). Declared-not-wired.
        ewa: true, // ADR-0009 D9-A9: EWA is a Growth-tier upsell (Growth + Enterprise). Declared-not-wired; NOT in BUILT_ until it ships (Phase 3+).
        time_tracking: true,
        expense_management: true,
        asset_management: false, // Enterprise-only
        training_module: false, // Enterprise-only
        performance_reviews: true,
        employee_self_service: true,
        api_access: false, // ADR-0016: API/integrations are Enterprise-only
        sso_integration: false, // Enterprise-only
        max_employees: null, // unlimited
    },
    enterprise: {
        scheduled_payroll: true, // reserved future feature
        multi_country_payroll: true,
        advanced_reports: true,
        ats: true,
        ewa: true, // EWA upsell present on Enterprise too (declared-not-wired)
        time_tracking: true,
        expense_management: true,
        asset_management: true,
        training_module: true,
        performance_reviews: true,
        employee_self_service: true,
        api_access: true,
        sso_integration: true,
        max_employees: null, // unlimited
    },
};
/**
 * The entitlement keys whose feature is actually SHIPPED (ADR-0016). The trial
 * (below) turns these on; the not-yet-built / Enterprise-plumbing flags
 * (`scheduled_payroll`, `training_module`, `api_access`, `sso_integration`)
 * stay off — there is nothing to try. Update this when a reserved module ships.
 *
 * NOTE (ADR-0024 Phase 4): `asset_management` is declared Enterprise-only in
 * TIER_DEFAULTS but enforced NOWHERE (asset routes carry no `requirePlan`) — so
 * this key gates nothing; it only surfaces the module to trials + lets the FE
 * show/hide the asset nav. Wiring enforcement is a separate FOUR-EYES change.
 */
export const BUILT_ENTITLEMENT_KEYS = [
    'multi_country_payroll',
    'advanced_reports',
    'ats',
    'ewa', // shipped in EWA Phase 3b (ADR-0009 D9) — trials now offer it
    'time_tracking',
    'performance_reviews',
    'expense_management', // shipped in ADR-0020 Phases 1–4 (D-10 "joins at ship") — trials now offer it
    'asset_management', // shipped in ADR-0024 Phases 1–4 ("joins at ship") — trials now offer it
    'employee_self_service',
];
/**
 * Trial entitlements (ADR-0016): during a 30-day trial a tenant gets **every
 * built feature** regardless of the plan on the subscription — so they can
 * experience the whole product before choosing a tier. The SEAT CAP is the one
 * thing that follows the plan rather than this set (see `resolveEntitlements`):
 * a Starter trial keeps the 50 cap, a Growth/Enterprise trial keeps unlimited.
 * This const carries the Starter-trial cap (50) as its default reference;
 * derived from BUILT_ENTITLEMENT_KEYS so the feature set can't drift.
 */
export const TRIAL_ENTITLEMENTS = Object.fromEntries(ENTITLEMENT_KEYS.map((key) => {
    if (key === 'max_employees')
        return [key, 50]; // Starter-trial reference; resolver swaps in the plan's cap
    return [key, BUILT_ENTITLEMENT_KEYS.includes(key)];
}));
/**
 * Resolve effective entitlements from tier + override map.
 *
 * Full-replacement semantics (locked decision Q3, 2026-06-08): for each
 * catalog key, the effective value is the override value IF that key is present
 * in `overrides`, otherwise the base value. An override value of `null` is a
 * deliberate "unlimited" and replaces the base (it is NOT treated as "absent").
 * Override keys outside the catalog are ignored.
 *
 * The base is the tier defaults, UNLESS `opts.trial` (ADR-0016) — then the base
 * is the all-built TRIAL_ENTITLEMENTS, so a trialing tenant gets everything
 * built regardless of plan, still overridable per tenant.
 */
export function resolveEntitlements(tier, overrides, opts) {
    // Trial: every built feature on, but the SEAT CAP follows the plan (ADR-0016)
    // — a Starter trial keeps the 50 cap; a Growth/Enterprise trial keeps its
    // unlimited seats. Features unlock; quotas follow the plan.
    const base = opts?.trial
        ? { ...TRIAL_ENTITLEMENTS, max_employees: TIER_DEFAULTS[tier].max_employees }
        : TIER_DEFAULTS[tier];
    const result = { ...base };
    if (!overrides)
        return result;
    for (const key of ENTITLEMENT_KEYS) {
        if (Object.prototype.hasOwnProperty.call(overrides, key)) {
            result[key] = overrides[key];
        }
    }
    return result;
}
//# sourceMappingURL=entitlements.js.map